import React, { useRef } from 'react'
import Dialog from '@mui/material/Dialog'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import { DialogContent, DialogTitle } from '@mui/material'
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop'
import { makeStyles } from '@mui/styles'
import { FormattedMessage } from 'react-intl'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'
import ButtonLoaderWrap from 'components/ui/ButtonLoaderWrap'
import { getBase64MimeType } from 'utils/mimeType'
import { ChallengeImageType } from 'api/types'

interface OwnProps {
  open?: boolean
  src?: null | string
  round?: boolean
  crop?: Crop
  aspect?: number
  outputMaxWidth?: number
  overlayImage?: string
  overlayRatio?: number
  onClose?(image?: Blob): void
  imageFieldName?: string
}

const useStyles = makeStyles(() => ({
  cropContainer: {
    '& .ReactCrop__drag-handle': {
      background: '#3872C5',
      borderRadius: '50%',
    },
  },
  overlayContainer: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pointerEvents: 'none' as const,
  },
  overlayImg: {
    borderRadius: '50%',
    border: '2px dashed rgba(255, 255, 255, 0.8)',
    objectFit: 'contain' as const,
    opacity: 0.7,
  },
}))

export default function CropImageDialog(props: OwnProps) {
  const classes = useStyles()
  const imageRef = useRef<HTMLImageElement>(null)
  const { open = false, round = false, src } = props
  const [crop, setCrop] = React.useState<Crop | undefined>(undefined)
  const [loading, setLoading] = React.useState(true)
  // Remote (http/https) sources need CORS so the image can be drawn onto a canvas without
  // tainting it. The dev S3 bucket doesn't send CORS headers, so route it through the same-origin
  // dev proxy instead; production's CDN already sends proper CORS headers.
  const isRemoteSrc = !!src && /^https?:\/\//.test(src)
  const isDevProxy = isRemoteSrc && process.env.NODE_ENV === 'development'
  // Outside the dev proxy, fetch remote images ourselves with `no-store` and render a same-origin blob URL.
  // Otherwise the browser may reuse a copy cached by a non-CORS <img> elsewhere, which has no CORS headers.
  const [blobSrc, setBlobSrc] = React.useState<string | undefined>(undefined)

  React.useEffect(() => {
    if (!src || !isRemoteSrc || isDevProxy) {
      return
    }
    let objectUrl: string | undefined
    let cancelled = false
    fetch(src, { mode: 'cors', cache: 'no-store' })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to load image: ${res.status}`)
        }
        return res.blob()
      })
      .then((blob) => {
        if (cancelled) {
          return
        }
        objectUrl = URL.createObjectURL(blob)
        setBlobSrc(objectUrl)
      })
      .catch((error) => console.log('Error loading image for cropping:', error))
    return () => {
      cancelled = true
      objectUrl && URL.revokeObjectURL(objectUrl)
      setBlobSrc(undefined)
    }
  }, [src, isRemoteSrc, isDevProxy])

  const displaySrc = isDevProxy ? `/s3-proxy?url=${encodeURIComponent(src ?? '')}` : isRemoteSrc ? blobSrc : src ?? ''

  const isCoverImageType = props.imageFieldName === ChallengeImageType.COVER

  const onClickSave = async () => {
    try {
      setLoading(true)
      if (imageRef.current && crop) {
        const croppedImg = await getCroppedImg(imageRef.current, crop)
        croppedImg && handleClose(croppedImg)
      }
    } catch (error) {
      console.log('Error cropping image:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = (image?: Blob): void => {
    const { onClose } = props
    if (onClose) {
      onClose(image)
      setCrop(undefined)
    }
  }

  const onImageLoaded = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Only make initial crop if aspect is provided.
    // Otherwise, use the crop provided in props.
    if (props.aspect) {
      const { naturalWidth, naturalHeight, width, height } = e.currentTarget
      const imageIsLandscape = naturalWidth >= naturalHeight

      // Super weird type but it does not work without this
      const cropSettings: Pick<PixelCrop, 'unit'> & Partial<Omit<PixelCrop, 'unit'>> = {
        unit: 'px',
        width: width,
      }

      // Situations where initial crop is based on height
      if (imageIsLandscape && props.aspect < 1) {
        cropSettings.width = undefined
        cropSettings.height = height
      } else if (!imageIsLandscape && props.aspect < 1) {
        cropSettings.width = undefined
        cropSettings.height = height
      } else if (imageIsLandscape && props.aspect === 1) {
        cropSettings.width = undefined
        cropSettings.height = height
      }

      const crop = centerCrop(makeAspectCrop(cropSettings, props.aspect, width, height), width, height)
      setCrop(crop)
    }

    setLoading(false)
  }

  const getCroppedImg = async (image: HTMLImageElement, crop: Crop) => {
    const type = getBase64MimeType(image.src) ?? 'image/png' // Fallback to PNG if undefined

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const drawSource: CanvasImageSource = image

    // Create an offscreen canvas for cropping
    const cropCanvas = new OffscreenCanvas(crop.width * scaleX, crop.height * scaleY)
    const cropCtx = cropCanvas.getContext('2d')

    if (!cropCtx) {
      throw new Error('No 2d context')
    }

    // Draw only the cropped area from the original image
    cropCtx.drawImage(
      drawSource,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      cropCanvas.width,
      cropCanvas.height,
    )

    if (!props.outputMaxWidth || cropCanvas.width <= props.outputMaxWidth) {
      return await cropCanvas.convertToBlob({
        type,
        quality: type === 'image/jpeg' ? 0.8 : undefined,
      })
    }

    // Downsizing, progressively resize the cropped area using another offscreen canvas.
    // https://stackoverflow.com/a/39637827
    const downsizeStep = 0.8
    let tempCanvas = cropCanvas

    while (tempCanvas.width * downsizeStep > props.outputMaxWidth) {
      const newWidth = Math.round(tempCanvas.width * downsizeStep)
      const newHeight = Math.round(tempCanvas.height * downsizeStep)

      const newCanvas = new OffscreenCanvas(newWidth, newHeight)
      const newCtx = newCanvas.getContext('2d')

      if (!newCtx) {
        throw new Error('No 2d context')
      }

      newCtx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, newWidth, newHeight)

      tempCanvas = newCanvas
    }

    // At this point the tempCanvas is a bit larger than the outputMaxWidth,
    // so we need to resize it one more time to the exact outputMaxWidth
    const newWidth = props.outputMaxWidth
    const newHeight = Math.round((tempCanvas.height / tempCanvas.width) * newWidth)

    const finalCanvas = new OffscreenCanvas(newWidth, newHeight)
    const finalCtx = finalCanvas.getContext('2d')

    if (!finalCtx) {
      throw new Error('No 2d context')
    }

    finalCtx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, newWidth, newHeight)

    // Convert the final resized cropped image to a Blob
    return await finalCanvas.convertToBlob({
      type,
      quality: type === 'image/jpeg' ? 0.8 : undefined,
    })
  }

  return (
    <Dialog maxWidth={'lg'} open={open} scroll={'body'} fullWidth keepMounted={false}>
      <DialogTitle id={'crop-image-dialog-title'}>
        <FormattedMessage id="strings.cropImage" />
      </DialogTitle>
      <DialogContent>
        <div
          style={{
            textAlign: 'center',
            background: '#dddddd',
            padding: '1em',
          }}
          className={classes.cropContainer}
        >
          {src && displaySrc && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <ReactCrop
                circularCrop={round}
                crop={props.aspect ? crop : props.crop}
                onChange={(crop) => setCrop(crop)}
                aspect={props.aspect}
              >
                <img
                  ref={imageRef}
                  src={displaySrc}
                  crossOrigin={isDevProxy ? 'anonymous' : undefined}
                  onLoad={onImageLoaded}
                />
              </ReactCrop>
              {props.overlayImage && crop && (
                <div
                  className={classes.overlayContainer}
                  style={{
                    top: crop.y,
                    left: crop.x,
                    right: 'auto',
                    bottom: 'auto',
                    width: crop.width,
                    height: crop.height,
                  }}
                >
                  <img
                    src={props.overlayImage}
                    className={classes.overlayImg}
                    style={{
                      position: isCoverImageType ? 'absolute' : undefined,

                      width: crop.width * (props.overlayRatio || 0.3),
                      height: crop.width * (props.overlayRatio || 0.3),

                      bottom: isCoverImageType ? -(crop.width * (props.overlayRatio || 0.3)) / 2 : undefined,

                      clipPath: isCoverImageType ? 'inset(0 0 50% 0)' : undefined,

                      overflow: isCoverImageType ? 'hidden' : undefined,

                      objectFit: 'contain',
                      borderRadius: isCoverImageType ? '50%' : undefined,
                      display: 'block',
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => handleClose()} style={{ marginRight: 10 }}>
          <FormattedMessageWrapper id={'strings.close'} />
        </Button>
        <ButtonLoaderWrap loading={loading}>
          <Button disabled={loading} color="primary" variant={'contained'} onClick={onClickSave}>
            <FormattedMessageWrapper id={'strings.save'} />
          </Button>
        </ButtonLoaderWrap>
      </DialogActions>
    </Dialog>
  )
}
